import React, { useEffect } from 'react';
import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { useAuth } from './contexts/AuthContext';
import Sidebar from './components/Sidebar';
import Login from './pages/Login';
import TrocarSenhaObrigatorio from './pages/TrocarSenhaObrigatorio';
import Home from './pages/Home';
import MeuPerfil from './pages/MeuPerfil';
import Usuarios from './pages/Usuarios';
import Perfis from './pages/Perfis';
import Clientes from './pages/Clientes';
import EnviarNotificacao from './pages/EnviarNotificacao';
import VerOrcamento from './pages/VerOrcamento';
import Pix from './pages/Pix';
import MinhasChavesPix from './pages/MinhasChavesPix';
import MinhasChavesPixDetalhe from './pages/MinhasChavesPixDetalhe';

function RequireAuth({ children }) {
  const { user, pronto } = useAuth();
  const loc = useLocation();
  if (!pronto) return <div className="loading-cli">Carregando…</div>;
  if (!user) return <Navigate to="/login" replace state={{ from: loc }} />;
  return children;
}

// Bloqueia o resto do portal enquanto a senha provisória estiver ativa.
// A tela TrocarSenhaObrigatorio permite "Pular por agora".
function SenhaProvisoriaGuard({ children }) {
  const { user } = useAuth();
  const loc = useLocation();
  if (user?.tipo === 'cliente' && user?.senha_provisoria && loc.pathname !== '/trocar-senha') {
    return <Navigate to="/trocar-senha" replace />;
  }
  return children;
}

function Layout({ children }) {
  return (
    <div className="layout-cli">
      <Sidebar />
      <main className="layout-main-cli">{children}</main>
    </div>
  );
}

function RotaInterna({ children, permissao }) {
  const { user, temPermissao } = useAuth();
  if (user?.tipo === 'cliente') return <Navigate to="/home" replace />;
  if (permissao && !temPermissao(permissao)) return <Navigate to="/home" replace />;
  return children;
}

// pagamento.centraomateriais.com so atende /pix — se o SPA navegar (client-side)
// para qualquer outra rota nesse host, faz um redirect duro para o portal
// principal. Cobre o caso em que o script inline do index.html nao pegou
// (bundle ja carregado, navegacao interna do react-router etc).
function GuardHostPagamento() {
  const loc = useLocation();
  useEffect(() => {
    if (typeof window === 'undefined') return;
    if (window.location.hostname !== 'pagamento.centraomateriais.com') return;
    if (/^\/pix\/?$/.test(loc.pathname)) return;
    window.location.replace(
      'https://clientes.centraomateriais.com'
      + loc.pathname
      + loc.search
      + loc.hash
    );
  }, [loc.pathname, loc.search, loc.hash]);
  return null;
}

export default function App() {
  const { user } = useAuth();

  return (
    <>
    <GuardHostPagamento />
    <Routes>
      {/* Rotas PUBLICAS (sem login) — visualizacao de orcamento/cotacao via link HMAC.
          Aceita tanto o formato canonico do sistema-suc (/publico/{tipo}/:id.pdf)
          quanto a versao curta (/p/{tipo}/:id). Em todos os casos a query string
          ?exp=&sig= e preservada pelo react-router. */}
      <Route path="/publico/orcamento/:id" element={<VerOrcamento tipo="orcamento" />} />
      <Route path="/publico/cotacao/:id" element={<VerOrcamento tipo="cotacao" />} />
      <Route path="/p/orcamento/:id" element={<VerOrcamento tipo="orcamento" />} />
      <Route path="/p/cotacao/:id" element={<VerOrcamento tipo="cotacao" />} />
      <Route path="/pix" element={<Pix />} />

      <Route path="/login" element={user ? <Navigate to="/home" replace /> : <Login />} />

      <Route
        path="/trocar-senha"
        element={
          <RequireAuth>
            <TrocarSenhaObrigatorio />
          </RequireAuth>
        }
      />

      <Route
        path="/home"
        element={
          <RequireAuth>
            <SenhaProvisoriaGuard>
              <Layout><Home /></Layout>
            </SenhaProvisoriaGuard>
          </RequireAuth>
        }
      />

      <Route
        path="/minhas-chaves-pix"
        element={
          <RequireAuth>
            <SenhaProvisoriaGuard>
              <Layout><MinhasChavesPix /></Layout>
            </SenhaProvisoriaGuard>
          </RequireAuth>
        }
      />

      <Route
        path="/minhas-chaves-pix/:id"
        element={
          <RequireAuth>
            <SenhaProvisoriaGuard>
              <Layout><MinhasChavesPixDetalhe /></Layout>
            </SenhaProvisoriaGuard>
          </RequireAuth>
        }
      />

      <Route
        path="/meu-perfil"
        element={
          <RequireAuth>
            <SenhaProvisoriaGuard>
              <Layout><MeuPerfil /></Layout>
            </SenhaProvisoriaGuard>
          </RequireAuth>
        }
      />

      <Route
        path="/usuarios"
        element={
          <RequireAuth>
            <Layout>
              <RotaInterna permissao="usuarios_listar"><Usuarios /></RotaInterna>
            </Layout>
          </RequireAuth>
        }
      />

      <Route
        path="/perfis"
        element={
          <RequireAuth>
            <Layout>
              <RotaInterna><Perfis /></RotaInterna>
            </Layout>
          </RequireAuth>
        }
      />

      <Route
        path="/clientes"
        element={
          <RequireAuth>
            <Layout>
              <RotaInterna permissao="clientes_listar"><Clientes /></RotaInterna>
            </Layout>
          </RequireAuth>
        }
      />

      <Route
        path="/notificacoes"
        element={
          <RequireAuth>
            <Layout>
              <RotaInterna permissao="notificacoes_enviar"><EnviarNotificacao /></RotaInterna>
            </Layout>
          </RequireAuth>
        }
      />

      <Route path="*" element={<Navigate to={user ? '/home' : '/login'} replace />} />
    </Routes>
    </>
  );
}
